import { create } from 'zustand';

export interface SlideInfo {
  slideId: string;
  title?: string;
  previewUrl?: string;
  status: 'pending' | 'complete' | 'error';
}

export type PreviewContent =
  | { type: 'outline'; data: Record<string, unknown> }
  | { type: 'slides'; projectId: string; slides: SlideInfo[] }
  | { type: 'video'; url: string; projectId: string }
  | { type: 'image'; url: string; alt: string };

interface LayoutState {
  sidebarCollapsed: boolean;
  sidebarSearchQuery: string;
  mobileMenuOpen: boolean;

  previewPanelOpen: boolean;
  previewPanelWidth: number;
  previewContent: PreviewContent | null;

  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setSidebarSearchQuery: (q: string) => void;
  setMobileMenuOpen: (open: boolean) => void;

  togglePreviewPanel: () => void;
  setPreviewPanelOpen: (open: boolean) => void;
  setPreviewPanelWidth: (w: number) => void;
  setPreviewContent: (content: PreviewContent | null) => void;
}

const STORAGE_KEY = 'ivy-chat-layout';

function loadPersistedState(): Partial<LayoutState> {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return {};
    const parsed = JSON.parse(stored);
    return {
      sidebarCollapsed: parsed.sidebarCollapsed ?? false,
      previewPanelWidth: parsed.previewPanelWidth ?? 40,
    };
  } catch {
    return {};
  }
}

function persistState(state: Pick<LayoutState, 'sidebarCollapsed' | 'previewPanelWidth'>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      sidebarCollapsed: state.sidebarCollapsed,
      previewPanelWidth: state.previewPanelWidth,
    }));
  } catch { /* noop */ }
}

const persisted = loadPersistedState();

export const useIvyChatLayoutStore = create<LayoutState>((set, get) => ({
  sidebarCollapsed: persisted.sidebarCollapsed ?? false,
  sidebarSearchQuery: '',
  mobileMenuOpen: false,

  previewPanelOpen: false,
  previewPanelWidth: persisted.previewPanelWidth ?? 40,
  previewContent: null,

  toggleSidebar: () => {
    const next = !get().sidebarCollapsed;
    set({ sidebarCollapsed: next });
    persistState({ ...get(), sidebarCollapsed: next });
  },

  setSidebarCollapsed: (collapsed) => {
    set({ sidebarCollapsed: collapsed });
    persistState({ ...get(), sidebarCollapsed: collapsed });
  },

  setSidebarSearchQuery: (q) => set({ sidebarSearchQuery: q }),

  setMobileMenuOpen: (open) => set({ mobileMenuOpen: open }),

  togglePreviewPanel: () => set(s => ({ previewPanelOpen: !s.previewPanelOpen })),

  setPreviewPanelOpen: (open) => set({ previewPanelOpen: open }),

  setPreviewPanelWidth: (w) => {
    const clamped = Math.max(20, Math.min(60, w));
    set({ previewPanelWidth: clamped });
    persistState({ ...get(), previewPanelWidth: clamped });
  },

  setPreviewContent: (content) => {
    set({ previewContent: content });
    if (content && !get().previewPanelOpen) {
      set({ previewPanelOpen: true });
    }
  },
}));
